import { AddressResponse, ApiError } from "@/types/types";
import { toast } from "react-toastify";
import pRetry from "p-retry";
import { ChangedFields, ListingData } from "./context/types";
import { sqftToM2 } from "@/lib/sqftToM2";
import { env } from "@/lib/env";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL;
const LISTINGSMANAGEMENTENDPOINTURL = env.NEXT_LISTINGS_MANAGEMENT_API_URL;

export const initPendingListingHandler = async (
  idToken: string | null,
  listingId: string,
) => {
  if (idToken === null) {
    toast("Please reauthenticate before performing this action.", {
      type: "error",
    });
    return;
  }
  const postWithRetry = async () => {
    const response = await fetch(
      `${LISTINGSMANAGEMENTENDPOINTURL}/initPendingListing`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          listing_id: listingId,
        }),
      },
    );
    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }
  };
  try {
    await pRetry(postWithRetry, {
      retries: 3,
      minTimeout: 100,
      maxTimeout: 1000,
      factor: 2,
      onFailedAttempt: (error) => {},
    });
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to initialize listing."
        : errorMessage,
      {
        type: "error",
      },
    );
    return;
  }
};

//return success / failure
export const editListingHandler = async (
  idToken: string | null,
  listing: ListingData,
  changedFields: ChangedFields,
  rate: number,
  unitSystem: string,
): Promise<EditListingResponse> => {
  if (idToken === null) {
    toast("Please reauthenticate before performing this action.", {
      type: "error",
    });
    return EditListingResponse.ERROR;
  }
  // if (changedFields.map_location === true) {
  const addressResponse = await fetch(
    `https://places.geo.eu-central-1.amazonaws.com/v2/reverse-geocode?key="Test"`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        QueryPosition: [listing.map_location?.lon, listing.map_location?.lat],
        MaxResults: 1,
        Language: "en",
      }),
    },
  );
  const addressesJson = (await addressResponse.json()) as AddressResponse;
  if (addressesJson.ResultItems.length > 0) {
    listing.city = addressesJson.ResultItems[0].Address.Locality;
    listing.country = addressesJson.ResultItems[0].Address.Country.Name;
    listing.region = addressesJson.ResultItems[0].Address.Region.Name ?? "";
    listing.sub_region =
      addressesJson.ResultItems[0].Address.SubRegion.Name ?? "";
    listing.country_code = addressesJson.ResultItems[0].Address.Country.Code2;
    if (addressesJson.ResultItems[0].Address.StreetComponents != null) {
      listing.address_basename =
        addressesJson.ResultItems[0].Address.StreetComponents[0].BaseName ?? "";
      listing.address_type =
        addressesJson.ResultItems[0].Address.StreetComponents[0].Type ?? "";
      listing.address_language =
        addressesJson.ResultItems[0].Address.StreetComponents[0].Language ?? "";
    } else {
      listing.address_basename =
        addressesJson.ResultItems[0].Address.Block ?? "";
      listing.address_language =
        addressesJson.ResultItems[0].Address.Block ?? "";
    }
    listing.address_number =
      addressesJson.ResultItems[0].Address.AddressNumber ?? "";
    changedFields.city = true;
    changedFields.country = true;
    changedFields.region = true;
    changedFields.sub_region = true;
    changedFields.country_code = true;
    changedFields.address_basename = true;
    changedFields.address_type = true;
    changedFields.address_language = true;
    changedFields.address_number = true;
  }
  // }
  listing.price = Math.round(listing.price * rate);
  if (unitSystem === "Imperial") {
    listing.interior_space = sqftToM2(listing.interior_space);
    listing.lot_size = sqftToM2(listing.lot_size);
    listing.total_structure_area = sqftToM2(listing.total_structure_area);
  }
  const postWithRetry = async () => {
    const response = await fetch(`${LISTINGSENDPOINTURL}/updateEditedListing`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken,
      },
      body: JSON.stringify({
        listing_data: listing,
        changed_fields: changedFields,
      }),
    });
    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }
  };
  try {
    await pRetry(postWithRetry, {
      retries: 3,
      minTimeout: 100,
      maxTimeout: 1000,
      factor: 2,
      onFailedAttempt: (error) => {},
    });
    return EditListingResponse.SUCCESS;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to edit your listing."
        : errorMessage,
      {
        type: "error",
      },
    );
    return EditListingResponse.ERROR;
  }
};

export enum EditListingResponse {
  SUCCESS = "success",
  ERROR = "error",
}
